/**
 * §03「つながりの構造」 部署レベルのトリニティ/V字算出（レベルA）
 *
 * 不足情報まとめ.md の論点に基づく実装。個人の有向エッジは知覚ベースONAでは得られないため、
 * O-13〜O-15「部署×部署の往来頻度マトリクス」から **部署レベル** の構造を算出する。
 *  - 三角形(トリニティ): 相互に往来のある3部署 = 「誰かが抜けてもカバーできる」健全構造
 *  - V字の底(ボトルネック部署): 媒介中心性が突出 = 多くがそこを介して繋がる構造的急所
 *
 * ※個人(田中さん等)の同定はこのデータでは不可。部署レベルに留める設計判断による
 *   （不足情報まとめ.md「§3 要決定」参照）。
 */

/** 部署×部署 往来頻度マトリクス */
export interface DeptInteractionMatrix {
  depts: string[];
  /** weight[i][j] = 部署i から見た部署j との往来頻度 (0〜1。i===j は無視) */
  weight: number[][];
}

export type DeptRole = 'bottleneck' | 'bridge' | 'triangle' | 'spoke' | 'isolated';

export interface DeptNode {
  dept: string;
  /** 次数中心性 (0〜1): 他部署との往来の広さ */
  degree: number;
  /** 媒介中心性 (0〜1): 他部署を仲介する度合い */
  betweenness: number;
  /** 役割分類 */
  role: DeptRole;
}

export interface DeptConnectionStructure {
  nodes: DeptNode[];
  /** 相互往来のある3部署 (健全な三角形) */
  triangles: string[][];
  /** V字の底 (ボトルネック部署)。無ければ null */
  bottleneckDept: string | null;
  /** 往来の乏しい孤立部署 */
  isolatedDepts: string[];
  /** 自動生成サマリ */
  summary: string;
}

/** エッジ採用の既定閾値 (対称化した往来頻度がこれ以上なら接続とみなす) */
const DEFAULT_EDGE_THRESHOLD = 0.45;

/** 対称隣接行列 (無向) を作る */
function toAdjacency(matrix: DeptInteractionMatrix, threshold: number): boolean[][] {
  const n = matrix.depts.length;
  const adj: boolean[][] = Array.from({ length: n }, () => new Array(n).fill(false));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const w = (matrix.weight[i][j] + matrix.weight[j][i]) / 2;
      if (w >= threshold) {
        adj[i][j] = true;
        adj[j][i] = true;
      }
    }
  }
  return adj;
}

/** 次数中心性 (0〜1) */
function degreeCentrality(adj: boolean[][]): number[] {
  const n = adj.length;
  if (n <= 1) return new Array(n).fill(0);
  return adj.map((row) => row.filter(Boolean).length / (n - 1));
}

/** 媒介中心性 (Brandes、無向・非重み)。正規化済 0〜1 */
function betweennessCentrality(adj: boolean[][]): number[] {
  const n = adj.length;
  const bc = new Array(n).fill(0);
  for (let s = 0; s < n; s++) {
    const stack: number[] = [];
    const pred: number[][] = Array.from({ length: n }, () => []);
    const sigma = new Array(n).fill(0);
    sigma[s] = 1;
    const dist = new Array(n).fill(-1);
    dist[s] = 0;
    const queue: number[] = [s];
    while (queue.length) {
      const v = queue.shift() as number;
      stack.push(v);
      for (let w = 0; w < n; w++) {
        if (!adj[v][w]) continue;
        if (dist[w] < 0) {
          dist[w] = dist[v] + 1;
          queue.push(w);
        }
        if (dist[w] === dist[v] + 1) {
          sigma[w] += sigma[v];
          pred[w].push(v);
        }
      }
    }
    const delta = new Array(n).fill(0);
    while (stack.length) {
      const w = stack.pop() as number;
      for (const v of pred[w]) {
        delta[v] += (sigma[v] / sigma[w]) * (1 + delta[w]);
      }
      if (w !== s) bc[w] += delta[w];
    }
  }
  // 無向なので2で割り、ペア数で正規化
  const norm = ((n - 1) * (n - 2)) / 2;
  return bc.map((v) => (norm > 0 ? v / 2 / norm : 0));
}

/** 3-クリーク (相互接続の3部署) を列挙 */
function findTriangles(adj: boolean[][], depts: string[]): string[][] {
  const n = adj.length;
  const out: string[][] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (!adj[i][j]) continue;
      for (let k = j + 1; k < n; k++) {
        if (adj[i][k] && adj[j][k]) out.push([depts[i], depts[j], depts[k]]);
      }
    }
  }
  return out;
}

/**
 * 部署×部署マトリクスから、部署レベルのつながり構造を算出する。
 */
export function computeDeptConnectionStructure(
  matrix: DeptInteractionMatrix,
  opts: { edgeThreshold?: number } = {}
): DeptConnectionStructure {
  const { depts } = matrix;
  const n = depts.length;
  const threshold = opts.edgeThreshold ?? DEFAULT_EDGE_THRESHOLD;
  const adj = toAdjacency(matrix, threshold);
  const degree = degreeCentrality(adj);
  const betweenness = betweennessCentrality(adj);
  const triangles = findTriangles(adj, depts);
  const inTriangle = new Set(triangles.flat());

  // ボトルネック: 媒介中心性が最大かつ有意 (>0)
  let bottleneckIdx = -1;
  let maxBtw = 0;
  betweenness.forEach((b, i) => {
    if (b > maxBtw) {
      maxBtw = b;
      bottleneckIdx = i;
    }
  });
  const bottleneckDept = maxBtw > 0 && bottleneckIdx >= 0 ? depts[bottleneckIdx] : null;

  const isolatedDepts: string[] = [];
  const nodes: DeptNode[] = depts.map((dept, i) => {
    let role: DeptRole;
    if (degree[i] === 0) {
      role = 'isolated';
      isolatedDepts.push(dept);
    } else if (i === bottleneckIdx && maxBtw > 0) {
      role = 'bottleneck';
    } else if (inTriangle.has(dept)) {
      role = 'triangle';
    } else if (betweenness[i] > 0) {
      role = 'bridge';
    } else {
      // 接続はあるが三角形に属さず仲介もしない = ハブに依存する「腕」(スポーク)
      role = 'spoke';
    }
    return { dept, degree: round2(degree[i]), betweenness: round2(betweenness[i]), role };
  });

  let summary: string;
  if (n < 3) {
    summary = '部署数が少ないため、つながりの構造は概況のみです。';
  } else if (bottleneckDept) {
    summary = `${bottleneckDept}が他部署をつなぐ要（V字の底）になっています。${bottleneckDept}に往来が集中しており、ここが抜けると連携が分断されやすい構造です。`;
  } else if (triangles.length > 0) {
    summary = `部署どうしが相互に往来できる三角形が${triangles.length}組あり、誰かが抜けてもカバーし合える健全な構造です。`;
  } else if (isolatedDepts.length > 0) {
    summary = `${isolatedDepts.join('・')}が他部署と往来が乏しく、孤立しがちです。橋渡しの接点を増やすと安定します。`;
  } else {
    summary = '部署間のつながりは概ね分散しており、特定の偏りは見られません。';
  }

  return { nodes, triangles, bottleneckDept, isolatedDepts, summary };
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
